import { matchSubscription } from '@ligr/shared/lib/Overlay/gql/subscriptions/match';
import { summarySubscription } from '@ligr/shared/lib/Overlay/gql/subscriptions/summary';
import { useLazyQuery, useSubscription, useApolloClient } from '@apollo/react-hooks';
import { useEffect, useState, useRef } from 'react';
import { MatchSubscriptionTopics } from '@ligr/api-v2';
import { OverlayContainer } from './overlayContainer';
import { OverlayHeartbeat } from '@ligr/shared';
import { createContainer } from 'unstated-next';
import moment from 'moment';
import { factsQuery } from '@ligr/shared/lib/Overlay/gql/queries/fact';
import { matchSummaryQuery } from '@ligr/shared/lib/Overlay/gql/queries/summary';
export const useDelayedSummaryWithHeartbeat = () => {
    const { skewMs } = OverlayHeartbeat.useContainer();
    const { setSkewMs } = DelayedSummary.useContainer();
    useEffect(() => {
        if (skewMs) {
            setSkewMs(skewMs);
        }
        else {
            setSkewMs(0);
        }
    }, [skewMs]);
};
export const useDelayedSummary = (inititalState) => {
    var _a;
    const [skewMs, setSkewMs] = useState(0);
    const { overlay } = OverlayContainer.useContainer();
    const client = useApolloClient();
    const timeouts = useRef([]);
    const [fetchSummary, summaryResult] = useLazyQuery(matchSummaryQuery, {
        fetchPolicy: 'network-only'
    });
    const [data, setData] = useState({
        matchSummary: undefined,
        newFacts: undefined,
        latestSummaryIndexSaved: 0
    });
    const [fetchFacts, { data: factData, loading: factsLoading }] = useLazyQuery(factsQuery);
    // Initialize all needed subscriptions
    useSubscription(matchSubscription, {
        variables: {
            matchSubscriptionArgs: {
                id: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId,
                topics: [MatchSubscriptionTopics.updates, MatchSubscriptionTopics.lineupUpdates]
            }
        }
    });
    const { data: latestFeedUpdate } = useSubscription(summarySubscription, {
        variables: { id: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId }
    });
    useEffect(() => {
        if (inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId) {
            fetchSummary({ variables: { matchId: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId } });
            fetchFacts({ variables: { matchId: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId } });
        }
    }, [inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId]);
    // Create timeouts that will with
    useEffect(() => {
        const date = moment();
        const adjustedDate = date.subtract(skewMs + ((overlay === null || overlay === void 0 ? void 0 : overlay.encodingDelay) || 0) * 1000, 'milliseconds');
        const factDate = (latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts.length) ? moment(latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts[0].date)
            : undefined;
        const updateIndex = timeouts.current.length + 1;
        timeouts.current.push({
            updateNumber: updateIndex,
            triggeringFact: latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts[0],
            timeout: setTimeout(() => {
                if (latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.matchSummary) {
                    setData(state => {
                        const newSummary = updateIndex > state.latestSummaryIndexSaved;
                        return {
                            matchSummary: newSummary ? latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.matchSummary : state.matchSummary,
                            newFacts: latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts,
                            latestSummaryIndexSaved: newSummary ? updateIndex : state.latestSummaryIndexSaved
                        };
                    });
                }
            }, Math.max(0, factDate ? factDate.valueOf() - adjustedDate.valueOf() : 0))
        });
    }, [latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed]);
    useEffect(() => {
        if (latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts.length) {
            let data;
            // NOTE: APOLLO DUMB CACHE INSANITY, waiting for fix.
            // Throws error if attempt to access cache for query that hasn't
            // been successfully performed.
            try {
                data = client.readQuery({
                    query: factsQuery,
                    variables: {
                        matchId: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId
                    }
                });
            }
            catch (error) {
                data = {
                    facts: []
                };
            }
            const addFactsToCache = (oldFacts, newFacts) => {
                const uniqueIds = new Set([...oldFacts.map(({ id }) => id), ...newFacts.map(({ id }) => id)]);
                return [...uniqueIds].map(foundId => {
                    return newFacts.find(({ id }) => id === foundId) || oldFacts.find(({ id }) => id === foundId);
                });
            };
            client.writeQuery({
                query: factsQuery,
                variables: {
                    matchId: inititalState === null || inititalState === void 0 ? void 0 : inititalState.matchId
                },
                data: {
                    facts: addFactsToCache(data.facts, latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts)
                }
            });
        }
    }, [latestFeedUpdate === null || latestFeedUpdate === void 0 ? void 0 : latestFeedUpdate.matchFeed.facts]);
    // Store the remote summary we fetch if the subscription hasn't returned a summary yet.
    useEffect(() => {
        var _a;
        if (!data.matchSummary) {
            setData({
                matchSummary: (_a = summaryResult.data) === null || _a === void 0 ? void 0 : _a.matchSummary,
                latestSummaryIndexSaved: 1
            });
        }
    }, [(_a = summaryResult.data) === null || _a === void 0 ? void 0 : _a.matchSummary]);
    // Remove the outstanding timeouts when unmounted
    useEffect(() => {
        return () => {
            timeouts.current.map(({ timeout }) => {
                clearTimeout(timeout);
            });
        };
    }, []);
    return { data, facts: factData === null || factData === void 0 ? void 0 : factData.facts, setSkewMs, skewMs: skewMs + ((overlay === null || overlay === void 0 ? void 0 : overlay.encodingDelay) || 0) * 1000 };
};
export const DelayedSummary = createContainer(useDelayedSummary);
export const DelayedSummaryWithClientSkewCorrection = createContainer(useDelayedSummaryWithHeartbeat);
//# sourceMappingURL=delayedSummaryContainer.js.map